<template>
  <div class="pp-tip-button">
    <Teleport
      v-if="isHover && $slots.content && !isMobile"
      :to="fileManagerEl || 'body'"
    >
      <div
        ref="contentElementRef"
        :style="style"
        class="pp-tip-button__content"
      >
        <slot name="content" />
      </div>
    </Teleport>
    <div
      ref="bodyElementRef"
      class="pp-tip-button__body"
    >
      <slot />
    </div>
  </div>
</template>

<script
  lang="ts"
  setup
>
import {
  useElementHover,
  useFileManagerEl,
  useIsMobile,
} from '@pikpak_web_player/hooks'
import { useElementSize } from '@vueuse/core'
import {
  computed,
  type CSSProperties,
  ref,
} from 'vue'

const { location, disabledInject } = defineProps<{
  location: 'top' | 'bottom';
  disabledInject?: boolean;
}>()

const fileManagerEl = disabledInject ? ref(null) : useFileManagerEl()
const bodyElementRef = ref<HTMLElement>()
const contentElementRef = ref<HTMLElement>()

const isMobile = disabledInject ? ref(false) : useIsMobile()

const { isHover } = useElementHover(bodyElementRef)
const { width: contentWidth, height: contentHeight } = useElementSize(
  contentElementRef,
  undefined,
  { box: 'border-box' },
)

// 安全距离
const SAFE_SIZE = 16

const getElementTopBody = (bodyRect: DOMRect, contentRect: DOMRect) => {
  const minLeft = SAFE_SIZE
  const maxLeft = window.innerWidth - SAFE_SIZE - contentRect.width

  const left = bodyRect.left - contentRect.width / 2 + bodyRect.width / 2
  const top = bodyRect.top - contentRect.height - 8

  return {
    left: left > maxLeft ? maxLeft : Math.max(minLeft, left),
    top,
  }
}

const getElementBottomBody = (bodyRect: DOMRect, contentRect: DOMRect) => {
  const minLeft = SAFE_SIZE
  const maxLeft = window.innerWidth - SAFE_SIZE - contentRect.width

  const left = bodyRect.left - contentRect.width / 2 + bodyRect.width / 2
  const top = bodyRect.top + bodyRect.height + 8

  return {
    left: left > maxLeft ? maxLeft : Math.max(minLeft, left),
    top,
  }
}

const style = computed(() => {
  const bodyRect = bodyElementRef.value?.getBoundingClientRect()

  const s: CSSProperties = {}

  if (!bodyRect || contentWidth.value === 0) return s

  if (location === 'top') {
    const { left, top } = getElementTopBody(
      bodyRect,
      { width: contentWidth.value, height: contentHeight.value } as DOMRect,
    )
    s.transform = `translate(${left}px, ${top}px)`
  }

  if (location === 'bottom') {
    const { left, top } = getElementBottomBody(
      bodyRect,
      { width: contentWidth.value, height: contentHeight.value } as DOMRect,
    )
    s.transform = `translate(${left}px, ${top}px)`
  }

  return s
})

const close = () => {
  isHover.value = false
}

defineExpose({
  close,
})

</script>

<style lang="less">
.pp-tip-button {
  position: relative;

  &__content {
    position: fixed;
    left: 0;
    top: 0;
    display: flex;
    color: #fff;
    font-size: 12px;
    line-height: 20px;
    background-color: #303133;
    border: 1px solid #303133;

    padding: 5px 11px;
    border-radius: 8px;
    white-space: nowrap;

    z-index: 1000;
  }

  &__body {
    width: 100%;
    height: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
  }
}
</style>
